/**
 * Enumerations shared by the API, the web client and the seed data.
 * Adding a value here is a data-model change: update docs/data-model.md too.
 */

export const RSVP_STATUSES = ['pending', 'attending', 'declined', 'maybe'] as const;
export type RsvpStatus = (typeof RSVP_STATUSES)[number];

/**
 * The couple's own guess at whether somebody will actually come, which is a
 * different question from what that person has replied.
 *
 * You invite the great-aunt in Cartagena knowing she almost certainly will not
 * fly, and the cousin who always says yes and never arrives. Both sit at
 * `pending` on the RSVP until they answer, and counting them the same way makes
 * the anticipated headcount — the number you book a venue against — a worse
 * guess than the one in your head.
 *
 * `unsure` is the default and means nobody has formed a view, not "fifty-fifty".
 */
export const GUEST_LIKELIHOODS = ['likely', 'unsure', 'unlikely'] as const;
export type GuestLikelihood = (typeof GUEST_LIKELIHOODS)[number];

/** Whose guest this is. Drives the "% by side" stat on the Guests tab. */
export const GUEST_SIDES = ['his', 'hers', 'his-parents', 'her-parents', 'shared'] as const;
export type GuestSide = (typeof GUEST_SIDES)[number];

export const VENUE_STATUSES = [
  'found',
  'contacted',
  'toured',
  'shortlisted',
  'booked',
  'rejected',
] as const;
export type VenueStatus = (typeof VENUE_STATUSES)[number];

export const VENDOR_STATUSES = ['researching', 'quoted', 'booked', 'paid'] as const;
export type VendorStatus = (typeof VENDOR_STATUSES)[number];

/** Seed list — a workspace may add its own categories later. Order is display order. */
export const VENDOR_CATEGORIES = [
  'venue',
  'catering',
  'bar',
  'photography',
  'videography',
  'flowers',
  'music-dj',
  'live-band',
  'entertainment',
  'cake-desserts',
  'attire',
  'hair-makeup',
  'stationery',
  'rentals-decor',
  'lighting',
  'transportation',
  'officiant',
  'accommodation',
  'favors',
  'planner-coordinator',
  'rings',
  'other',
] as const;
export type VendorCategory = (typeof VENDOR_CATEGORIES)[number];

/**
 * The vendor categories a venue might cover, plus the few things only a venue
 * has.
 *
 * Deliberately a subset of `VENDOR_CATEGORIES` rather than a list of its own:
 * when Vendors is built, a venue marked "photography: extra" and a Vendor of
 * category `photography` are talking about the same thing, and a parallel
 * taxonomy would make that impossible to line up. The four venue-only keys have
 * no vendor counterpart because you do not hire a parking lot.
 */
const INCLUDABLE_VENDOR_CATEGORIES = [
  'catering',
  'bar',
  'cake-desserts',
  'music-dj',
  'live-band',
  'entertainment',
  'photography',
  'videography',
  'flowers',
  'rentals-decor',
  'lighting',
  'planner-coordinator',
  'accommodation',
  'transportation',
  'officiant',
] as const satisfies readonly VendorCategory[];

const VENUE_ONLY_INCLUSIONS = ['ceremony-space', 'parking', 'cleanup', 'security'] as const;

export const VENUE_INCLUSION_KEYS = [
  ...INCLUDABLE_VENDOR_CATEGORIES,
  ...VENUE_ONLY_INCLUSIONS,
] as const;
export type VenueInclusionKey = (typeof VENUE_INCLUSION_KEYS)[number];

/**
 * `not_offered` is not the same as `extra`: one means bring your own, the other
 * means the venue will not have it at all, which can be a dealbreaker rather
 * than a cost. A category nobody has answered yet is simply absent from the
 * list — "unanswered" is the absence of a row, not a fourth status.
 */
export const INCLUSION_STATUSES = ['included', 'extra', 'not_offered'] as const;
export type InclusionStatus = (typeof INCLUSION_STATUSES)[number];

/**
 * Words seen on venue sites and in the old free-text `includes`/`excludes`
 * fields, mapped to a key. Only used to read legacy data and to place the
 * model's hints; nothing depends on it being exhaustive.
 */
export const VENUE_INCLUSION_ALIASES: Record<string, VenueInclusionKey> = {
  food: 'catering',
  catering: 'catering',
  caterer: 'catering',
  banquet: 'catering',
  menu: 'catering',
  dinner: 'catering',
  bar: 'bar',
  alcohol: 'bar',
  drinks: 'bar',
  liquor: 'bar',
  bartender: 'bar',
  open_bar: 'bar',
  cake: 'cake-desserts',
  dessert: 'cake-desserts',
  desserts: 'cake-desserts',
  dj: 'music-dj',
  music: 'music-dj',
  band: 'live-band',
  live_music: 'live-band',
  entertainment: 'entertainment',
  photographer: 'photography',
  photography: 'photography',
  photos: 'photography',
  videographer: 'videography',
  videography: 'videography',
  video: 'videography',
  flowers: 'flowers',
  florist: 'flowers',
  floral: 'flowers',
  decor: 'rentals-decor',
  decoration: 'rentals-decor',
  decorations: 'rentals-decor',
  centerpieces: 'rentals-decor',
  tables: 'rentals-decor',
  chairs: 'rentals-decor',
  tables_and_chairs: 'rentals-decor',
  tables_chairs: 'rentals-decor',
  furniture: 'rentals-decor',
  linens: 'rentals-decor',
  linen: 'rentals-decor',
  tablecloths: 'rentals-decor',
  rentals: 'rentals-decor',
  lighting: 'lighting',
  lights: 'lighting',
  coordination: 'planner-coordinator',
  coordinator: 'planner-coordinator',
  planner: 'planner-coordinator',
  day_of_coordinator: 'planner-coordinator',
  accommodation: 'accommodation',
  rooms: 'accommodation',
  lodging: 'accommodation',
  suite: 'accommodation',
  transportation: 'transportation',
  shuttle: 'transportation',
  transport: 'transportation',
  officiant: 'officiant',
  minister: 'officiant',
  ceremony: 'ceremony-space',
  ceremony_space: 'ceremony-space',
  chapel: 'ceremony-space',
  altar: 'ceremony-space',
  parking: 'parking',
  valet: 'parking',
  cleanup: 'cleanup',
  cleaning: 'cleanup',
  security: 'security',
  guard: 'security',
};

/**
 * Checklist categories. Deliberately not `VENDOR_CATEGORIES`: a task is a thing
 * to do, and "find out what the marriage licence needs" belongs to no supplier.
 * The two lists answer different questions and lining them up would force a
 * paperwork task to pretend it had a vendor.
 */
export const TASK_CATEGORIES = [
  'budget',
  'venue',
  'vendors',
  'guests',
  'attire',
  'beauty',
  'ceremony',
  'reception',
  'paperwork',
  'honeymoon',
  'day-of',
  'other',
] as const;
export type TaskCategory = (typeof TASK_CATEGORIES)[number];

export const TABLE_SHAPES = ['round', 'rect', 'sweetheart'] as const;
export type TableShape = (typeof TABLE_SHAPES)[number];

export const CONSTRAINT_KINDS = ['together', 'apart'] as const;
export type ConstraintKind = (typeof CONSTRAINT_KINDS)[number];

/** owner and partner may write; family reads and reviews; planner writes on behalf of a couple. */
export const MEMBER_ROLES = ['owner', 'partner', 'family', 'planner'] as const;
export type MemberRole = (typeof MEMBER_ROLES)[number];

/**
 * Caterers price these three differently and often do not charge for an infant,
 * and seating wants the children together. Every body is a Guest row, so this is
 * how the guest list answers "how many meals, and of what kind".
 */
export const AGE_BANDS = ['adult', 'child', 'infant'] as const;
export type AgeBand = (typeof AGE_BANDS)[number];

export const WORKSPACE_KINDS = ['couple', 'planner'] as const;
export type WorkspaceKind = (typeof WORKSPACE_KINDS)[number];

export const PLANS = ['free', 'couple', 'couple-plus', 'planner'] as const;
export type Plan = (typeof PLANS)[number];

/** Roles allowed to mutate wedding data. Checked in the API authorizer layer. */
export const WRITE_ROLES: readonly MemberRole[] = ['owner', 'partner', 'planner'];

/**
 * Collaborators a plan allows, excluding the owner — the `Collaborators` column
 * of the pricing table in docs/plan.md. `null` is unlimited.
 *
 * Billing is Phase 3, so nothing reads a real subscription yet and every
 * workspace is `free`. The cap matters now anyway: it is the limit on how many
 * Cognito accounts one workspace can ever cause to be created.
 */
export const COLLABORATOR_CAPS: Record<Plan, number | null> = {
  free: 2,
  couple: 5,
  'couple-plus': null,
  planner: null,
};

/**
 * Venues a plan allows. `null` is unlimited.
 *
 * Three is enough to compare a shortlist and not enough to run the search in
 * Guabi, which is the line Free is meant to sit on.
 */
export const VENUE_CAPS: Record<Plan, number | null> = {
  free: 3,
  couple: null,
  'couple-plus': null,
  planner: null,
};

/**
 * The features a plan turns on, from the pricing table in docs/plan.md §8.
 *
 * A record rather than scattered `plan === 'free'` checks: the pricing table
 * changes, and when it does there should be exactly one place that disagrees
 * with the old one.
 */
export interface PlanFeatures {
  /** The automatic seating pass. Arranging tables by hand is free. */
  seatingAi: boolean;
  /** Publishing the public page. Writing it is free; serving it is not. */
  rsvpSite: boolean;
  customDomain: boolean;
  /** Removes "planned with Guabi" from the public page. */
  whiteLabel: boolean;
}

export const PLAN_FEATURES: Record<Plan, PlanFeatures> = {
  free: { seatingAi: false, rsvpSite: false, customDomain: false, whiteLabel: false },
  couple: { seatingAi: true, rsvpSite: true, customDomain: false, whiteLabel: false },
  'couple-plus': { seatingAi: true, rsvpSite: true, customDomain: true, whiteLabel: false },
  planner: { seatingAi: true, rsvpSite: true, customDomain: true, whiteLabel: true },
};

/**
 * Guests a plan allows. Counts people, not households — a plus-one and a child
 * are real rows now, so the old figure of 40 would have bought half a wedding.
 * Unnamed placeholders do not count; you have not invited anyone in particular yet.
 * `null` is unlimited. Nothing enforces this until billing lands in Phase 3.
 */
export const GUEST_CAPS: Record<Plan, number | null> = {
  free: 80,
  couple: null,
  'couple-plus': null,
  planner: null,
};

/**
 * Model tokens a plan allows per calendar month, from the pricing table in
 * docs/plan.md §7. `null` is unlimited.
 *
 * Worth knowing what Free buys: one photographed page costs roughly 5–10k
 * tokens, so 50k is five to ten photos a month. That is deliberate — the cap is
 * meant to be felt — but it means a long address book will not finish in one
 * sitting, and the import screen shows what is left before you start.
 */
export const AI_TOKEN_BUDGETS: Record<Plan, number | null> = {
  free: 50_000,
  couple: 500_000,
  'couple-plus': null,
  planner: null,
};

/** Invites one workspace may send per day, to bound accidental or abusive fan-out. */
export const INVITES_PER_DAY = 10;

/** Money is integer cents everywhere — never a float, never a string. */
export const DEFAULT_CURRENCY = 'USD';
